import type { ReactNode } from "react";
import { RiQuestionLine } from "react-icons/ri";
import { IdentitySwitchMenu, MemberCircleList } from "@components/IdentitySwitchMenu";
import { isHomeHaze, HAZE_HERO_INK } from "./homeSkin";

/** 不規則 header 形狀變體（viewBox 0 0 390 280） */
export type ColdHeaderShapeKey = "hero" | "wave" | "dip" | "slant" | "scoop" | "soft";

export const COLD_HEADER_SHAPE_OPTIONS: {
  key: ColdHeaderShapeKey;
  label: string;
  desc: string;
}[] = [
  { key: "hero", label: "有機雙層", desc: "有機曲線＋半透明雙層（採用）" },
  { key: "wave", label: "波浪", desc: "多段起伏" },
  { key: "dip", label: "中央下凹", desc: "中間較低" },
  { key: "slant", label: "斜切", desc: "左高右低" },
  { key: "scoop", label: "單弧", desc: "一道大弧" },
  { key: "soft", label: "柔邊", desc: "較平緩" },
];

const SHAPES: Record<
  ColdHeaderShapeKey,
  {
    path: string;
    overlap: number;
    /** 半透明疊層路徑（雙層波） */
    layer?: string;
    /** 有機曲線 morph 的第二個路徑（會在 path↔morph 間循環） */
    morph?: string;
    /** 半透明疊層的 morph 路徑（與主層不同 dur → 有機錯拍） */
    layerMorph?: string;
    /** header 最小高度（加高版覆蓋卡片上緣） */
    minH?: number;
  }
> = {
  // 採用版：有機曲線 morph ＋ 半透明雙層，加高讓下緣微蓋住卡片；擺幅加大、雙層錯拍動更明顯
  hero: {
    overlap: -70,
    minH: 288,
    layer: `M0,0 H390 V208 C318,250 250,206 178,232 C108,258 54,224 0,244 Z`,
    layerMorph: `M0,0 H390 V200 C318,222 250,258 178,232 C108,206 54,250 0,232 Z`,
    path: `M0,0 H390 V196 C356,254 320,212 262,238 C210,262 190,280 132,258 C92,242 44,268 0,246 Z`,
    morph: `M0,0 H390 V222 C356,218 320,264 262,242 C210,222 190,250 132,272 C92,284 44,240 0,266 Z`,
  },
  wave: {
    overlap: -52,
    path: `M0,0 H390 V200
           C340,235 300,188 245,215
           C185,245 130,195 75,220
           C40,235 18,215 0,228 Z`,
  },
  dip: {
    overlap: -48,
    path: `M0,0 H390 V195
           C330,195 300,195 260,210
           C210,240 180,240 130,210
           C90,190 50,195 0,200 Z`,
  },
  slant: {
    overlap: -44,
    path: `M0,0 H390 V175
           C300,175 240,210 170,225
           C100,240 50,245 0,250 Z`,
  },
  scoop: {
    overlap: -56,
    path: `M0,0 H390 V185
           C280,250 110,250 0,195 Z`,
  },
  soft: {
    overlap: -40,
    path: `M0,0 H390 V205
           C300,220 200,228 100,220
           C50,216 20,212 0,210 Z`,
  },
};

/** 預設採用版：有機曲線＋雙層波（可由 DEV 強制其他形狀） */
export function pickColdHeaderShape(force?: ColdHeaderShapeKey | "auto" | null): ColdHeaderShapeKey {
  if (force && force !== "auto") return force;
  return "hero";
}

/**
 * 冷啟動・全新客戶 Header
 * 品牌藍＋柔邊下緣（預設 soft）；問卷卡輕微疊上。
 */
export const ColdStartNewHero = ({
  onWhyNoPrediction,
  children,
  shape = "hero",
  aiForecastEnabled = true,
}: {
  onWhyNoPrediction: () => void;
  children?: ReactNode;
  /** 預設 hero（有機雙層）；auto 亦回 hero；其餘僅 DEV 強制指定 */
  shape?: ColdHeaderShapeKey | "auto";
  /**
   * 此瓦斯行是否開放 AI 叫桶預測。關閉時問卷照問（答案仍用於了解用氣習慣），
   * 但不可承諾「之後會變成 AI 預測」，「為什麼還沒有智慧預測」的說明也要收起來
   * ——那張說明頁會解釋 AI 怎麼運作，等於又把 AI 講回來。
   */
  aiForecastEnabled?: boolean;
}) => {
  const shapeKey = pickColdHeaderShape(shape);
  const { path, overlap, layer, layerMorph, morph, minH } = SHAPES[shapeKey];
  const gradId = `coldHdrBright_${shapeKey}`;
  /* 淡霧版：造型頭的漸層換成淡藍、文字轉深藍。
     這個 hero 是一塊有機曲線的色塊（SVG path），不是矩形色帶，所以淡霧要畫進 SVG 的
     linearGradient 裡而不是外層 div；上面那層 rgba(255,255,255,0.14) 的裝飾層在淡底上
     等於消失，改成同色系的淡藍才看得出兩層。 */
  const haze = isHomeHaze();

  return (
    <div style={{ position: "relative", background: "#f8fafc" }}>
      <div style={{ position: "relative", minHeight: minH ?? 268 }}>
        <svg
          aria-hidden
          viewBox="0 0 390 288"
          preserveAspectRatio="none"
          style={{
            position: "absolute",
            inset: 0,
            width: "100%",
            height: "100%",
            display: "block",
          }}>
          <defs>
            <linearGradient id={gradId} x1="0" y1="0" x2="0.2" y2="1">
              <stop offset="0%" stopColor={haze ? "#cfe7f9" : "#42A5F5"} />
              <stop offset="100%" stopColor={haze ? "#e8f2fb" : "#1E88E5"} />
            </linearGradient>
          </defs>
          {layer && (
            <path fill={haze ? "rgba(120,170,220,0.16)" : "rgba(255,255,255,0.14)"} d={layer}>
              {layerMorph && (
                <animate
                  attributeName="d"
                  dur="7s"
                  repeatCount="indefinite"
                  calcMode="spline"
                  keyTimes="0;0.5;1"
                  keySplines="0.42 0 0.58 1;0.42 0 0.58 1"
                  values={`${layer};${layerMorph};${layer}`}
                />
              )}
            </path>
          )}
          <path fill={`url(#${gradId})`} d={path}>
            {morph && (
              <animate
                attributeName="d"
                dur="4.5s"
                repeatCount="indefinite"
                calcMode="spline"
                keyTimes="0;0.5;1"
                keySplines="0.42 0 0.58 1;0.42 0 0.58 1"
                values={`${path};${morph};${path}`}
              />
            )}
          </path>
        </svg>

        <div style={{ position: "relative", zIndex: 1, padding: "14px 18px 0" }}>
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              minHeight: 40,
            }}>
            <IdentitySwitchMenu haze={haze} />
            <MemberCircleList haze={haze} />
          </div>

          <div style={{ padding: "28px 2px 72px", color: haze ? HAZE_HERO_INK : "#fff" }}>
            <div style={{ fontSize: 13, fontWeight: 600, opacity: 0.9, marginBottom: 8 }}>
                讓我們先認識您
            </div>
            <div
              style={{
                fontSize: 24,
                fontWeight: 800,
                lineHeight: 1.3,
                letterSpacing: -0.2,
              }}>
              {aiForecastEnabled ? "智慧叫桶提醒設定" : "用氣習慣小調查"}
            </div>
            <div
              style={{
                marginTop: 8,
                fontSize: 13.5,
                lineHeight: 1.5,
                opacity: 0.92,
              }}>
              {aiForecastEnabled
                ? "回答幾題問題，之後會依您實際叫桶自動調整為 AI 預測。"
                : "回答幾題問題，幫助我們更了解您的用氣習慣。"}
            </div>
          </div>
        </div>
      </div>

      {children && (
        <div
          style={{
            position: "relative",
            zIndex: 2,
            marginTop: overlap,
            padding: "0 16px 4px",
          }}>
          {children}
        </div>
      )}

      {aiForecastEnabled && (
      <div style={{ padding: "4px 20px 2px" }}>
        <button
          type="button"
          onClick={onWhyNoPrediction}
          style={{
            display: "inline-flex",
            alignItems: "center",
            gap: 5,
            background: "none",
            border: "none",
            color: "#8b909c",
            fontSize: 12.5,
            fontWeight: 600,
            cursor: "pointer",
            fontFamily: "inherit",
            padding: "2px 0",
          }}>
          <RiQuestionLine size={14} color="#8b909c" />
          為什麼還沒有智慧預測？
        </button>
      </div>
      )}
    </div>
  );
};
